import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, ButtonGroup, Button, OverlayTrigger, Tooltip, Card } from 'react-bootstrap';
import { getTranslations, getRatings, getTypologicalAverage } from '@lib/api';

import { Ico98 } from '@icons/new';
import { TooltipInfoIcon } from '@icons';
import ESGRadarCardsComponent from './esgRadarComponent';

const EMPTY = 'N/A';

function ESGPanel({ project = {}, translations = [], ratings = [], isMonitoring = false }) {
  const esgRatings = useMemo(() => project.esgratings ?? [], [project.esgratings]);
  const femaleIndicator = project.femaleaud ?? null;

  // tipologico (per singolo progetto)
  const [typologicalAverageEsg, setTypologicalAverageEsg] = useState(EMPTY);
  const [typologicalScoringEsg, setTypologicalScoringEsg] = useState(EMPTY);

  useEffect(() => {
    if (!project?.id) return;
    let cancelled = false;
    getTypologicalAverage(project.id).then((res) => {
      if (cancelled) return;
      const avg = res?.response;
      setTypologicalAverageEsg(avg ? (avg.avgEsgRating ?? EMPTY) : EMPTY);
      setTypologicalScoringEsg(
        avg
          ? (avg.avgEsgScore?.toLocaleString('it-IT', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            }) ?? EMPTY)
          : EMPTY,
      );
    });
    return () => {
      cancelled = true;
    };
  }, [project?.id]);

  const sortedRatings = useMemo(() => [...ratings].sort((a, b) => a.min - b.min), [ratings]);

  const scoreESG = useMemo(() => {
    const score = esgRatings.filter((r) => r.type === 'total').map((r) => r.scorePerc);
    if (!score.length) return EMPTY;
    return String(score[0]).replace('.', ','); // come prima
  }, [esgRatings]);

  const projectRatings = useMemo(() => {
    if (!esgRatings?.length) return {};
    const pick = (cat) =>
      esgRatings
        .filter((r) => r.type === 'category' && r.category === cat)
        .map((r) => r.rating)
        .join();
    const total = esgRatings
      .filter((r) => r.type === 'total')
      .map((r) => r.rating)
      .join();
    return { esg: total, E: pick('E'), S: pick('S'), G: pick('G') };
  }, [esgRatings]);

  const numberOfDuplicate = useMemo(() => {
    const { esg, ...rest } = projectRatings;
    const vals = Object.values(rest).filter(Boolean);
    if (!vals.length) return 0;
    const counts = vals.reduce((acc, v) => ((acc[v] = (acc[v] || 0) + 1), acc), {});
    const max = Math.max(...Object.values(counts));
    return max > 1 ? max : 0;
  }, [projectRatings]);

  const renderTooltip = (props) => {
    let message = '';
    if (props.popper?.state) message = props.popper.state.options.testObj;
    return (
      <Tooltip id='tooltip-disabled' {...props}>
        {message}
      </Tooltip>
    );
  };

  const TitleWithTooltip = ({ code }) => (
    <Card.Title className='margin-title-box text-uppercase pt-3'>
      {translations.filter((t) => t.code === code).map((t) => t.text)}{' '}
      {isMonitoring && 'del progetto monitorato'}
      <OverlayTrigger
        placement='right'
        delay={{ show: 200, hide: 400 }}
        overlay={
          <Tooltip id='button-tooltip'>
            {translations.filter((t) => t.code === code).map((t) => t.description)}
          </Tooltip>
        }>
        <TooltipInfoIcon className='tooltip-ico mb-1' />
      </OverlayTrigger>
    </Card.Title>
  );

  const SummaryTable = () => (
    <table className='mt-2 ml-auto' style={{ fontSize: '1.5rem' }} id='dashboardTable'>
      <thead>
        <tr className='py-2'>
          <td className='pr-4 pb-4'></td>
          <td className='pr-4 pb-4 esg-header columns'>Puntuale</td>
          <td className='pr-4 pb-4 esg-header columns'>Tipologico</td>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td className='pr-4 pb-4 esg-header rows text-uppercase'>SCORING</td>
          <td className='pr-4 pb-4 f-600'>{scoreESG}</td>
          <td className='pr-4 pb-4'>{typologicalScoringEsg}</td>
        </tr>
        <tr>
          <td className='pr-4 pb-4 esg-header rows text-uppercase'>RATING</td>
          <td className='pr-4 pb-4 f-600'>{projectRatings.esg ?? EMPTY}</td>
          <td className='pr-4 pb-4'>{typologicalAverageEsg}</td>
        </tr>
      </tbody>
    </table>
  );

  const RatingPill = ({ rating }) => {
    const bg =
      rating.outlook === 'red'
        ? 'red'
        : rating.outlook === 'yellow'
          ? 'yellow'
          : rating.outlook === 'green'
            ? 'green'
            : 'grey';
    return (
      <Button className={`button-group-base ${bg}`} size='lg' disabled>
        {rating.code}
      </Button>
    );
  };

  const RatingsStrip = () => (
    <ButtonGroup className='buttongroup-esg'>
      {sortedRatings.map((r) => (
        <RatingPill key={r.code} rating={r} />
      ))}
    </ButtonGroup>
  );

  const CategoryIndicators = () => {
    const { esg, ...cats } = projectRatings;
    if (!Object.keys(cats).length) return null;

    return (
      <ButtonGroup
        className='buttongroup-esg indicator align-items-end'
        id='buttongroup-indicators'>
        {sortedRatings.map((rating) => {
          const codes = Object.keys(cats).filter((k) => cats[k] === rating.code);
          if (codes.length) {
            return (
              <Button
                key={rating.code}
                className='button-group-rating showRatingESG rounded'
                size='lg'
                disabled>
                {codes.map((code) => {
                  const score = esgRatings
                    .filter((r) => r.type === 'category' && r.category === code)
                    .map((r) => r.scorePerc.toLocaleString('it-IT'));
                  return (
                    <OverlayTrigger
                      key={code}
                      placement='top'
                      delay={{ show: 200, hide: 400 }}
                      overlay={renderTooltip}
                      popperConfig={{ testObj: score }}>
                      <span id={rating.code}>
                        {code}
                        <div className='arrow-down'></div>
                      </span>
                    </OverlayTrigger>
                  );
                })}
              </Button>
            );
          }
          return (
            <Button
              key={rating.code}
              id={rating.code}
              variant='outline-dark'
              className='button-group-rating'
              size='lg'
              disabled
            />
          );
        })}
      </ButtonGroup>
    );
  };

  const ESGTotalIndicator = () => {
    const score = esgRatings
      .filter((r) => r.type === 'total')
      .map((r) => r.scorePerc.toLocaleString('it-IT'));

    return (
      <ButtonGroup className='buttongroup-esg indicator'>
        {sortedRatings.map((rating) =>
          rating.code === projectRatings.esg ? (
            <OverlayTrigger
              key={rating.code}
              placement='top'
              delay={{ show: 200, hide: 400 }}
              overlay={renderTooltip}
              popperConfig={{ testObj: score }}>
              <span className='d-inline-block no-to-head'>
                <Button
                  className='button-group-rating showRatingESG rounded'
                  size='lg'
                  disabled
                  style={{ pointerEvents: 'none' }}>
                  <div className='arrow-up'></div>ESG
                </Button>
              </span>
            </OverlayTrigger>
          ) : (
            <Button
              key={rating.code}
              variant='outline-dark'
              className='button-group-rating'
              size='lg'
              disabled
            />
          ),
        )}
      </ButtonGroup>
    );
  };

  const FemaleIndicatorTable = () =>
    project.proponentCode === 'AUD' && femaleIndicator ? (
      <table className='mt-2 mr-auto' style={{ fontSize: '1.5rem' }}>
        <thead>
          <tr className='py-2'>
            <td className='pr-0 pb-4 esg-header columns'></td>
            <td className='pb-4 esg-header columns'>
              {translations.filter((t) => t.code === 'ICS114').map((t) => t.text)}
            </td>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className='pr-4 pb-4 esg-header rows text-uppercase'>SCORING</td>
            <td className='pr-4 pb-4 f-600'>{femaleIndicator.scorePerc}%</td>
          </tr>
          <tr>
            <td className='pr-4 pb-4 esg-header rows text-uppercase'>RATING</td>
            <td className='pr-4 pb-4 f-600'>{femaleIndicator.rating}</td>
          </tr>
        </tbody>
      </table>
    ) : null;

  return (
    <Card className='cards-utente h-100'>
      <Card.Body>
        <Ico98 className='logo-card ico-box-blue' />
        <TitleWithTooltip code='ICS038' />

        <Row className='px-2 mb-4 d-flex flex-column'>
          <Col className='pr-0'>
            <SummaryTable />
          </Col>

          <Col
            className='esg-infobox text-center pl-0 mt-3'
            style={{
              flexGrow: 2,
              marginTop:
                numberOfDuplicate === 0
                  ? '0.5rem'
                  : numberOfDuplicate === 2
                    ? '-1.5rem'
                    : '-3.5rem',
            }}>
            <div className='d-flex flex-column'>
              <div>
                <CategoryIndicators />
              </div>

              <div className='mt-2 mb-2'>
                <RatingsStrip />
              </div>

              <div>
                <ESGTotalIndicator />
              </div>
            </div>
          </Col>

          <Col className='pr-0 mt-3'>
            <FemaleIndicatorTable />
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
}

const ESGDetails = ({ project, compareProject }) => {
  const [translations, setTranslations] = useState([]);
  const [ratings, setRatings] = useState([]);

  useEffect(() => {
    getTranslations().then(setTranslations);
    getRatings().then(setRatings);
  }, []);

  return (
    <>
      {/* desktop: due card affiancate */}
      <Row className='d-none d-lg-flex g-4' style={{ marginTop: '1.8rem' }}>
        <Col className='mb-5' style={{ minWidth: '750px' }}>
          <ESGPanel project={project} translations={translations} ratings={ratings} />
        </Col>

        <Col className='mb-5' style={{ minWidth: '750px' }}>
          <ESGPanel
            project={compareProject}
            translations={translations}
            ratings={ratings}
            isMonitoring={true}
          />
        </Col>
      </Row>

      {
        <ESGRadarCardsComponent
          project={project}
          compareProject={compareProject}
          translations={translations}
        />
      }
    </>
  );
};

export default ESGDetails;
